// Resolves where a mode switch should land so the user keeps their place.
//
// Switching modes used to always drop you on the mode's landing page. When the
// current entity has an equivalent page in the target mode (a domain, product or
// process shown in both business and live), we navigate to that same entity
// instead. Dev mode is intentionally not preserved — switching to/from dev goes
// to the landing page.

type Mode = "business" | "live" | "dev";

type RouteParams = Record<string, unknown>;

type StekBase = { orgName: string; stekName: string };

// Entity kinds that exist in more than one mode, keyed by the route param that
// identifies them. Only business/live have per-entity pages; dev is omitted, so
// it always falls back to its landing page.
const ENTITY_ROUTES: Array<{
    param: string;
    routes: Partial<Record<Mode, string>>;
}> = [
    {
        param: "domainId",
        routes: { business: "domainPage", live: "liveDomainPage" },
    },
    {
        param: "productId",
        routes: { business: "productPage", live: "liveProductPage" },
    },
    {
        param: "processId",
        routes: { business: "processPage", live: "liveProcessPage" },
    },
];

const LANDING: Record<Mode, string> = {
    business: "stekPage",
    live: "livePage",
    dev: "devPage",
};

// Reverse lookup: a mapped route name → the entity definition it belongs to.
const ROUTE_TO_ENTITY = new Map<string, (typeof ENTITY_ROUTES)[number]>();
for (const entity of ENTITY_ROUTES) {
    for (const routeName of Object.values(entity.routes)) {
        if (routeName) ROUTE_TO_ENTITY.set(routeName, entity);
    }
}

export function resolveModeTarget(
    targetMode: Mode,
    currentRouteName: string | null | undefined,
    params: RouteParams,
    base: StekBase
): { name: string; params: RouteParams } {
    const landing = {
        name: LANDING[targetMode],
        params: { orgName: base.orgName, stekName: base.stekName },
    };

    const entity = currentRouteName
        ? ROUTE_TO_ENTITY.get(currentRouteName)
        : undefined;
    if (!entity) return landing;

    const targetName = entity.routes[targetMode];
    const id = params[entity.param];
    if (!targetName || id == null) return landing;

    return {
        name: targetName,
        params: {
            orgName: base.orgName,
            stekName: base.stekName,
            [entity.param]: id,
        },
    };
}
